import {
  Card,
  CardDescription,
  CardFooter,
  CardHeader,
} from '@/components/ui/card';
import KeyboardKeyIcon from '@/components/ui/keyboard-key-icon';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/hooks/use-toast';
import { HOTKEYS } from '@/lib/hotkeys';
import { pdkAxios } from '@/lib/pdk-axios';
import { cn } from '@/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@radix-ui/react-tooltip';
import { useMutation } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { times } from 'lodash';
import { ArrowUpCircle } from 'lucide-react';
import { motion } from 'motion/react';
import { useEffect, useRef, useState, type RefObject } from 'react';

const DEFAULT_DE_ID_MESSAGE = `
  Hey @TJ Casner,

  Just wanted to update you on the Robert Johnson deal. I spoke with his wife Julia Johnson earlier today about his vehicle purchase. They want the 2021 Hyundai Kona Limited ($19399.00)
  
Here are his details for the financing application:

  Customer: Robert Johnson
  Email: rjohnson@example.com
  Phone: (555) 234-5678
  DOB: 03/15/1985
  Address: 42421 Oak St San Francisco, CA 94102

  He's trading in his current vehicle (VIN: 1FDKF37G1VEB89551) and applying for financing. His credit looks good - he provided:
  - SSN: 489-36-8350
  - Driver's License: I1234568 (CA)
  - Passport: 910239248

  For the down payment, he's using:
  - Credit Card: 4532-1234-5678-9012
  - Bank Account: 987654321
  - Routing: 121000358

Sincerely @Jimmy Truong 
`;

const AddChatMessage = ({
  message,
  setMessage,
  handleSubmitComment,
}: {
  message: string;
  setMessage: (message: string) => void;
  handleSubmitComment: () => void;
}) => {
  return (
    <Card className="p-4 flex flex-col gap-4">
      <CardHeader className="p-0">
        <CardDescription>
          <Textarea
            placeholder="Enter your message (de-id)"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={(e) => {
              if (
                (e.key === 'Enter' && e.metaKey) ||
                (e.key === 'Enter' && e.ctrlKey)
              ) {
                e.preventDefault();
                e.stopPropagation();

                handleSubmitComment();
              }
            }}
          />
        </CardDescription>
      </CardHeader>
      <CardFooter className="p-0">
        <motion.div
          className="flex flex-row w-full justify-end gap-2 h-10 items-center"
          initial={{ height: 0, opacity: 0 }}
          animate={{
            height: 'auto',
            opacity: 1,
          }}
          transition={{ duration: 0.3 }}
        >
          <Tooltip>
            <TooltipTrigger asChild>
              <ArrowUpCircle
                className={cn(
                  'h-5 w-5 cursor-pointer text-gray-500 hover:fill-gray-100',
                )}
                onClick={handleSubmitComment}
              />
            </TooltipTrigger>
            <TooltipContent className="flex flex-row gap-1 items-center">
              <p>Submit Comment</p>
              <KeyboardKeyIcon>{HOTKEYS.DEAL_SUBMIT_COMMENT}</KeyboardKeyIcon>
            </TooltipContent>
          </Tooltip>
        </motion.div>
      </CardFooter>
    </Card>
  );
};

export const useDeId = ({
  startTime,
  setIsStarted,
  setIsFinished,
  endTime,
}: {
  startTime?: RefObject<number>;
  setIsStarted?: (isStarted: boolean) => void;
  setIsFinished?: (isFinished: boolean) => void;
  endTime?: RefObject<number>;
}) => {
  const deIdMutation = useMutation({
    mutationFn: async (message: string): Promise<string> => {
      if (startTime) {
        startTime.current = dayjs().valueOf();
      }
      setIsStarted?.(true);
      setIsFinished?.(false);
      const response = await pdkAxios.post<string>(
        '/ml/de-id',

        {
          message,
        },
        {
          timeout: Infinity,
        },
      );

      if (endTime) {
        endTime.current = dayjs().valueOf();
      }

      setIsFinished?.(true);

      return response.data;
    },

    onError: (error) => {
      setIsFinished?.(true);
      toast({
        title: 'Error De-IDing the message',
        description: error.message,
      });
    },
  });

  return deIdMutation;
};

const DeId = () => {
  const startTime = useRef<number>(dayjs().valueOf());
  const endTime = useRef<number>(dayjs().valueOf());
  const [isStarted, setIsStarted] = useState(false);
  const [isFinished, setIsFinished] = useState(false);
  const [currentDuration, setCurrentDuration] = useState(0);

  const deIdMutation = useDeId({
    startTime,
    setIsStarted,
    setIsFinished,
    endTime,
  });

  useEffect(() => {
    if (isStarted && !isFinished) {
      const interval = setInterval(() => {
        setCurrentDuration(dayjs().diff(startTime.current, 'ms'));
      }, 10);

      return () => clearInterval(interval);
    }
  }, [isStarted, isFinished]);

  const [message, setMessage] = useState(DEFAULT_DE_ID_MESSAGE);

  const handleSubmitComment = () => {
    deIdMutation.mutate(message);
  };

  return (
    <div className="flex flex-col gap-4 p-4">
      <AddChatMessage
        message={message}
        setMessage={setMessage}
        handleSubmitComment={handleSubmitComment}
      />

      {currentDuration > 0 && (
        <div className="flex flex-col">
          <div className="text-sm text-gray-500">
            Loading... {currentDuration}ms{' '}
          </div>
          <div className="wrap-anywhere flex text-wrap whitespace-normal">
            {times(currentDuration / 33, () => '.').join('')} 🐌
          </div>
        </div>
      )}

      {deIdMutation.isSuccess && (
        <div className="flex flex-col">
          <div className="text-sm text-gray-500">
            <Card className="p-4 flex flex-col gap-4">
              <CardHeader className="p-0">
                <CardDescription>
                  <Textarea
                    placeholder="De-ID"
                    value={deIdMutation.data}
                    readOnly
                  />
                </CardDescription>
                <CardFooter className="p-2 border rounded-md flex flex-col gap-2 text-gray-500 items-start">
                  <div>message length: {message.length} characters</div>
                  <div>
                    response length: {deIdMutation.data.length} characters
                  </div>
                  <div>
                    response time: {endTime.current - startTime.current}ms
                  </div>
                </CardFooter>
              </CardHeader>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeId;
